// Zeiterfassung am Ticket: Start/Stopp, Nachbuchen und das Protokoll.
//
// Der Timer läuft serverseitig (ticket_zeiten mit offenem Ende) — er überlebt also
// einen Seitenwechsel oder ein zugeklapptes Notebook. Pro Mitarbeiter kann nur EIN
// Ticket-Timer laufen; das sichert ein eindeutiger Teilindex in der Datenbank.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClockCounterClockwise, FilePdf, PencilSimple, Play, Receipt, SpinnerGap, Stop, Trash, WarningCircle, X } from '@phosphor-icons/react';
import { IosSchalter } from '../ui/IosSchalter';
import { AppIcon, AppKachel } from '../ui/AppIconKnopf';
import { NACHWEIS_LEER, nachweisMoeglich, timerText } from '../../lib/tickets/darstellung';
import {
  ladeTicketZeiten, laufenderTicketTimer, ticketZeitLoeschen, ticketZeitSpeichern,
  timerStarten, timerStoppen,
} from '../../lib/zeiterfassung/api';
import { aufteilung, buchungFehler, zeitMinuten, type TicketZeit } from '../../lib/zeiterfassung/ticketzeit';
import { stundenText, tagText, tagVon, uhrzeit } from '../../lib/zeiterfassung/berechnung';
import { exportZeitnachweisPdf, type ZeitnachweisTicket } from '../../lib/export/exportZeitnachweisPdf';
import { useTicketTimer } from '../../contexts/TicketTimerContext';
import { supabase } from '../../lib/supabase/client';

interface Props {
  ticketId: string;
  userProfile?: { id?: string; full_name?: string; role?: string } | null;
  /** Namen der Mitarbeiter für die Protokollzeilen */
  namen?: Record<string, string>;
  /** Kopfdaten für den Zeitnachweis. Fehlen sie, bleibt der Nachweis-Knopf verborgen. */
  ticket?: ZeitnachweisTicket;
  /** Anzahl Buchungen (Zähler im Einsatz-Tab) */
  onAnzahl?: (n: number) => void;
}

const FELD: React.CSSProperties = {
  height: 38, borderRadius: 11, padding: '0 10px', border: 'none', outline: 'none', background: 'var(--bg)',
  boxShadow: 'inset 0 0 0 1px var(--line)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13.5, minWidth: 0,
};
const LABEL: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--ink3)' };

const TicketZeiten: React.FC<Props> = ({ ticketId, userProfile, namen = {}, ticket, onAnzahl }) => {
  const meineId = userProfile?.id || '';
  const [zeiten, setZeiten] = useState<TicketZeit[]>([]);
  const [timer, setTimer] = useState<TicketZeit | null>(null);
  const [jetzt, setJetzt] = useState(() => new Date());
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [taetigkeit, setTaetigkeit] = useState('');
  const [abrechenbar, setAbrechenbar] = useState(true);
  const [arbeitet, setArbeitet] = useState(false);
  const [formular, setFormular] = useState<{ id?: string; tag: string; von: string; bis: string; text: string; abrechenbar: boolean } | null>(null);
  const [geladeneNamen, setGeladeneNamen] = useState<Record<string, string>>({});
  // Der Chip in der Kopfleiste lebt vom selben Timer — Start und Stopp meldet er sofort
  // weiter, statt auf den Minutentakt des Kontexts zu warten.
  const { neuLaden: chipNeuLaden, timer: pillenTimer } = useTicketTimer();

  const laden = useCallback(async () => {
    setLaedt(true);
    const [liste, offen] = await Promise.all([
      ladeTicketZeiten(ticketId),
      meineId ? laufenderTicketTimer(meineId) : Promise.resolve(null),
    ]);
    setZeiten(liste);
    setTimer(offen);
    setLaedt(false);

    // Namen der Buchenden nachladen, wenn der Aufrufer keine mitgibt — sonst bliebe die
    // Spalte im Protokoll UND im Zeitnachweis leer.
    const ids = [...new Set(liste.map((z) => z.mitarbeiter_id))];
    if (ids.length > 0) {
      const { data } = await supabase.from('profiles').select('id, full_name').in('id', ids);
      setGeladeneNamen(Object.fromEntries((data || []).map((p) => [p.id as string, (p.full_name as string) || ''])));
    }
  }, [ticketId, meineId]);

  useEffect(() => { void laden(); }, [laden]);
  // Start/Stopp an anderer Stelle (Pille, anderes Gerät): Liste nachziehen, sobald der globale Timer abweicht.
  const pillenTimerId = pillenTimer?.zeit.id ?? null;
  const timerId = timer?.id ?? null;
  useEffect(() => {
    if (!laedt && pillenTimerId !== timerId) void laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pillenTimerId]);
  useEffect(() => { onAnzahl?.(zeiten.length); }, [zeiten.length, onAnzahl]);

  useEffect(() => {
    if (!timer) return;
    const t = setInterval(() => setJetzt(new Date()), 1000);
    return () => clearInterval(t);
  }, [timer]);

  const meinTimerHier = timer && timer.ticket_id === ticketId ? timer : null;
  const timerWoanders = timer && timer.ticket_id !== ticketId;

  const summe = useMemo(() => aufteilung(zeiten, jetzt), [zeiten, jetzt]);
  const alleNamen = useMemo(() => ({ ...geladeneNamen, ...namen }), [geladeneNamen, namen]);

  const starten = async () => {
    if (!meineId) return;
    setArbeitet(true);
    setFehler(null);
    const problem = await timerStarten(ticketId, meineId, abrechenbar);
    setArbeitet(false);
    if (problem) { setFehler(problem); return; }
    await Promise.all([laden(), chipNeuLaden()]);
  };

  const stoppen = async () => {
    if (!meinTimerHier) return;
    setArbeitet(true);
    const ok = await timerStoppen(meinTimerHier.id, taetigkeit);
    setArbeitet(false);
    if (ok) { setTaetigkeit(''); await Promise.all([laden(), chipNeuLaden()]); }
  };

  const speichern = async () => {
    if (!formular || !meineId) return;
    const problem = buchungFehler(formular);
    if (problem) { setFehler(problem); return; }
    setFehler(null);
    setArbeitet(true);
    const ok = await ticketZeitSpeichern({
      id: formular.id,
      ticketId,
      mitarbeiterId: meineId,
      beginn: new Date(`${formular.tag}T${formular.von}:00`).toISOString(),
      ende: new Date(`${formular.tag}T${formular.bis}:00`).toISOString(),
      taetigkeit: formular.text,
      abrechenbar: formular.abrechenbar,
    });
    setArbeitet(false);
    if (!ok) { setFehler('Speichern fehlgeschlagen.'); return; }
    setFormular(null);
    await laden();
  };

  const nachweisLaden = () => {
    if (!ticket) return;
    setFehler(null);
    if (!nachweisMoeglich(zeiten) || !exportZeitnachweisPdf(ticket, zeiten, alleNamen)) {
      setFehler(NACHWEIS_LEER);
    }
  };

  const loeschen = async (z: TicketZeit) => {
    if (!window.confirm(`Buchung vom ${tagText(tagVon(z.beginn))} (${stundenText(zeitMinuten(z))}) löschen?`)) return;
    await ticketZeitLoeschen(z.id);
    // Auch der laufende Timer kann hier gelöscht werden — sonst zeigte der Chip weiter
    // eine Buchung an, die es nicht mehr gibt.
    await Promise.all([laden(), chipNeuLaden()]);
  };

  const ichName = userProfile?.full_name || alleNamen[meineId] || '';
  const formDauer = formular && formular.von && formular.bis && formular.bis > formular.von
    ? stundenText((Number(formular.bis.slice(0, 2)) * 60 + Number(formular.bis.slice(3, 5))) - (Number(formular.von.slice(0, 2)) * 60 + Number(formular.von.slice(3, 5))))
    : null;
  const abrAn = meinTimerHier ? meinTimerHier.abrechenbar : abrechenbar;

  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      {fehler && (
        <div role="alert" className="flex items-start" style={{ gap: 8, padding: '9px 12px', borderRadius: 12, background: fehler === NACHWEIS_LEER ? 'var(--warn-soft)' : 'var(--bad-soft)',
          color: fehler === NACHWEIS_LEER ? 'var(--warn)' : 'var(--bad)', fontSize: 12.5, fontWeight: 700 }}>
          <WarningCircle size={16} weight="fill" className="shrink-0" />
          <span className="flex-1">{fehler}</span>
          <button type="button" onClick={() => setFehler(null)} aria-label="Hinweis schließen" style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0 }}>
            <X size={13} weight="bold" />
          </button>
        </div>
      )}

      {/* Timer-Kachel */}
      <div className="flex flex-wrap items-center" style={{ gap: 12, padding: 12, borderRadius: 18,
        background: meinTimerHier ? 'var(--ok-soft)' : 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
        <button type="button" onClick={() => void (meinTimerHier ? stoppen() : starten())}
          disabled={arbeitet || (!meinTimerHier && !!timerWoanders)}
          aria-label={meinTimerHier ? 'Timer stoppen' : 'Timer starten'} data-tip={meinTimerHier ? 'Timer stoppen' : 'Timer starten'}
          className="inline-flex shrink-0 items-center justify-center"
          style={{ width: 48, height: 48, borderRadius: '50%', border: 'none', cursor: 'pointer', color: '#fff',
            background: meinTimerHier ? 'var(--bad)' : 'var(--ok)', boxShadow: `0 10px 22px -10px ${meinTimerHier ? 'var(--bad)' : 'var(--ok)'}`,
            opacity: arbeitet || (!meinTimerHier && timerWoanders) ? 0.45 : 1 }}>
          {arbeitet ? <SpinnerGap size={20} className="animate-spin" /> : meinTimerHier ? <Stop size={20} weight="fill" /> : <Play size={20} weight="fill" />}
        </button>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="tabular-nums" style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
            {meinTimerHier ? timerText(jetzt.getTime() - Date.parse(meinTimerHier.beginn)) : '0:00:00'}
          </span>
          <span className="truncate" style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>
            {meinTimerHier ? `Timer läuft${ichName ? ` · ${ichName}` : ''}` : timerWoanders ? 'Ein Timer läuft bereits auf einem anderen Ticket.' : 'Timer starten'}
          </span>
        </div>
        <div className="flex shrink-0 flex-col items-end" style={{ gap: 3 }}>
          <span style={LABEL}>Gesamt</span>
          <span className="tabular-nums" style={{ fontSize: 15, fontWeight: 800 }}>{stundenText(summe.gesamt)}</span>
          {summe.abrechenbar > 0 && (
            <span data-tip="Abrechenbar – Diese Zeit erscheint im Zeitnachweis für den Kunden" className="inline-flex items-center"
              style={{ gap: 4, height: 20, padding: '0 7px', borderRadius: 10, fontSize: 10.5, fontWeight: 800, background: 'var(--ok-soft)', color: 'var(--ok)' }}>
              <Receipt size={11} weight="fill" /> {stundenText(summe.abrechenbar)} abrechenbar
            </span>
          )}
        </div>
        {meinTimerHier && (
          <input value={taetigkeit} onChange={(e) => setTaetigkeit(e.target.value)} placeholder="Was wurde gemacht?" aria-label="Tätigkeit"
            style={{ ...FELD, width: '100%', flexBasis: '100%' }} />
        )}
      </div>

      {/* Abrechenbar (gilt für den nächsten Start; während der Timer läuft gesperrt) */}
      <div className="flex items-center" style={{ gap: 10, padding: '10px 12px', borderRadius: 16, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
        <AppIcon icon={Receipt} farbe="gruen" groesse={30} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span style={{ fontSize: 13.5, fontWeight: 800, opacity: meinTimerHier ? 0.6 : 1 }}>Abrechenbar</span>
          <span className="truncate" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink3)' }}>
            {meinTimerHier ? 'Während der Timer läuft nicht änderbar' : abrAn ? 'Erscheint im Zeitnachweis' : 'Nur intern, nicht im Nachweis'}
          </span>
        </span>
        <IosSchalter an={abrAn} onChange={setAbrechenbar} label="Abrechenbar" gesperrt={!!meinTimerHier}
          tip="Abrechenbar – Gestartete Zeit wird dem Kunden in Rechnung gestellt und erscheint im Zeitnachweis" />
      </div>

      {/* Nachtragen / Korrigieren */}
      {formular && (
        <div className="flex flex-col" style={{ gap: 8, padding: 12, borderRadius: 16, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1.5px var(--acc)' }}>
          <div className="grid" style={{ gap: 8, gridTemplateColumns: '1.3fr 1fr 1fr' }}>
            <label className="flex min-w-0 flex-col" style={{ gap: 4 }}><span style={LABEL}>Tag</span>
              <input type="date" value={formular.tag} onChange={(e) => setFormular({ ...formular, tag: e.target.value })} style={FELD} data-tip="Tag – An welchem Tag wurde gearbeitet?" /></label>
            <label className="flex min-w-0 flex-col" style={{ gap: 4 }}><span style={LABEL}>Von</span>
              <input type="time" value={formular.von} onChange={(e) => setFormular({ ...formular, von: e.target.value })} style={FELD} data-tip="Von – Beginn der Arbeit" /></label>
            <label className="flex min-w-0 flex-col" style={{ gap: 4 }}><span style={LABEL}>Bis</span>
              <input type="time" value={formular.bis} onChange={(e) => setFormular({ ...formular, bis: e.target.value })} style={FELD} data-tip="Bis – Ende der Arbeit" /></label>
          </div>
          <input value={formular.text} onChange={(e) => setFormular({ ...formular, text: e.target.value })} placeholder="Tätigkeit (optional)" aria-label="Tätigkeit"
            style={FELD} data-tip="Tätigkeit – Was wurde gemacht?" />
          <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
            <span className="inline-flex items-center" style={{ gap: 8, fontSize: 13, fontWeight: 700 }}>
              <IosSchalter klein an={formular.abrechenbar} onChange={(an) => setFormular({ ...formular, abrechenbar: an })} label="Abrechenbar"
                tip="Abrechenbar – Zeit erscheint im Zeitnachweis für den Kunden" /> Abrechenbar
            </span>
            {formDauer && <span className="tabular-nums" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink3)' }}>Dauer {formDauer}</span>}
            <span className="ml-auto inline-flex" style={{ gap: 6 }}>
              <button type="button" onClick={() => setFormular(null)} style={{ height: 34, padding: '0 12px', borderRadius: 17, border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700 }}>
                Abbrechen
              </button>
              <button type="button" onClick={() => void speichern()} disabled={arbeitet} data-tip="Speichern – Zeit auf das Ticket buchen"
                style={{ height: 34, padding: '0 14px', borderRadius: 17, border: 'none', cursor: 'pointer', background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, opacity: arbeitet ? 0.6 : 1 }}>
                Speichern
              </button>
            </span>
          </div>
        </div>
      )}

      {/* Protokoll */}
      {laedt ? (
        <div className="flex justify-center" style={{ padding: '16px 0', color: 'var(--ink3)' }}><SpinnerGap size={16} className="animate-spin" /></div>
      ) : zeiten.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>Noch keine Zeiten gebucht.</p>
      ) : (
        <div className="flex flex-col">
          {zeiten.map((z) => (
            // Zwei Zeilen (Ticket #500): oben die Eckdaten, darunter die Tätigkeit über die volle Breite.
            <div key={z.id} className="group" style={{ padding: '8px 2px', borderBottom: '1px solid var(--line)' }}>
              <div className="flex flex-wrap items-center" style={{ gap: '4px 10px', fontSize: 12.5 }}>
                <span style={{ fontWeight: 800 }}>{tagText(tagVon(z.beginn))}</span>
                <span className="tabular-nums" style={{ color: 'var(--ink3)', fontWeight: 600 }}>{uhrzeit(z.beginn)}–{z.ende ? uhrzeit(z.ende) : 'läuft'}</span>
                <span className="tabular-nums" style={{ fontWeight: 800 }}>{stundenText(zeitMinuten(z, jetzt))}</span>
                {z.abrechenbar && (
                  <span data-tip="Abrechenbar" style={{ height: 18, padding: '0 6px', borderRadius: 9, fontSize: 10, fontWeight: 800, lineHeight: '18px', background: 'var(--ok-soft)', color: 'var(--ok)' }}>abr.</span>
                )}
                <span className="ml-auto truncate" style={{ fontSize: 11.5, color: 'var(--ink3)' }}>{alleNamen[z.mitarbeiter_id] || ''}</span>
                {z.mitarbeiter_id === meineId && z.ende && (
                  <span className="inline-flex items-center" style={{ gap: 2 }}>
                    <button type="button" aria-label="Bearbeiten" data-tip="Bearbeiten – Eigenen Zeiteintrag ändern"
                      onClick={() => setFormular({ id: z.id, tag: tagVon(z.beginn), von: uhrzeit(z.beginn), bis: uhrzeit(z.ende), text: z.taetigkeit || '', abrechenbar: z.abrechenbar })}
                      className="inline-flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink2)' }}>
                      <PencilSimple size={13} weight="bold" />
                    </button>
                    <button type="button" aria-label="Löschen" data-tip="Löschen" onClick={() => void loeschen(z)}
                      className="inline-flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--bad-soft)', color: 'var(--bad)' }}>
                      <Trash size={13} weight="bold" />
                    </button>
                  </span>
                )}
              </div>
              <p className="whitespace-pre-wrap break-words" style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--ink2)' }}>
                {z.taetigkeit || <span style={{ fontStyle: 'italic', color: 'var(--ink3)' }}>ohne Beschreibung</span>}
              </p>
            </div>
          ))}
        </div>
      )}

      {!meinTimerHier && (
        <div className="grid" style={{ gap: 8, gridTemplateColumns: ticket ? 'repeat(2, minmax(0,1fr))' : 'minmax(0,1fr)' }}>
          <AppKachel icon={ClockCounterClockwise} farbe="orange" titel="Nachtragen" unter="Zeit ohne Timer"
            tip="Nachtragen – Zeit ohne Timer erfassen (Tag, Von, Bis)"
            onClick={() => setFormular({ tag: tagVon(new Date().toISOString()), von: '09:00', bis: '10:00', text: '', abrechenbar: true })} />
          {/* Der Nachweis geht an den Kunden — deshalb NUR die abrechenbaren Zeiten. */}
          {ticket && <AppKachel icon={FilePdf} farbe="rot" titel="Nachweis" unter="PDF für den Kunden" tip="Nachweis – Zeitnachweis als PDF, nur abrechenbare Zeiten" onClick={nachweisLaden} />}
        </div>
      )}
    </div>
  );
};

export default TicketZeiten;
